// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/ui/components/Slider.module.css';
import type {MarkerState, SliderMarkerPosition} from '@app/features/ui/components/slider/SliderTypes';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import KeyboardMode from '@app/features/ui/state/KeyboardMode';
import {clsx} from 'clsx';
import type React from 'react';

interface SliderTrackProps {
	sliderRef: React.RefObject<HTMLDivElement | null>;
	thumbRef: React.RefObject<HTMLDivElement | null>;
	markerState: MarkerState;
	value: number;
	valueScaled: number;
	factoryDefaultValue: number;
	disabled: boolean;
	mini: boolean;
	className?: string;
	markerPosition: SliderMarkerPosition;
	barStyles: React.CSSProperties;
	fillStyles: React.CSSProperties;
	ariaLabelledBy?: string;
	ariaValueText?: string;
	sliderLabel: string;
	onMarkerRender?: (value: number) => React.ReactNode;
	onTrackMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
	onThumbMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
	onThumbTouchStart: (event: React.TouchEvent<HTMLDivElement>) => void;
	onThumbMouseEnter: () => void;
	onThumbMouseLeave: () => void;
	onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
	children?: React.ReactNode;
}

export function SliderTrack({
	sliderRef,
	thumbRef,
	markerState,
	value,
	valueScaled,
	factoryDefaultValue,
	disabled,
	mini,
	className,
	markerPosition,
	barStyles,
	fillStyles,
	ariaLabelledBy,
	ariaValueText,
	sliderLabel,
	onMarkerRender,
	onTrackMouseDown,
	onThumbMouseDown,
	onThumbTouchStart,
	onThumbMouseEnter,
	onThumbMouseLeave,
	onKeyDown,
	children,
}: SliderTrackProps): React.ReactElement {
	const hasMarks = markerState.markerPositions.length > 0;
	const valuePercent = `${valueScaled}%`;
	const combinedFillStyles = {
		...fillStyles,
		width: valuePercent,
	};
	return (
		<FocusRing ringTarget={thumbRef} offset={4} data-flx="ui.slider.slider-track.focus-ring">
			<div
				ref={sliderRef}
				className={clsx(
					styles.slider,
					mini && styles.mini,
					hasMarks && styles.hasMarks,
					disabled && styles.disabled,
					className,
				)}
				role="slider"
				aria-valuemin={markerState.min}
				aria-valuemax={markerState.max}
				aria-valuenow={value}
				aria-valuetext={ariaValueText}
				aria-disabled={disabled}
				aria-label={ariaLabelledBy ? undefined : sliderLabel}
				aria-labelledby={ariaLabelledBy}
				tabIndex={disabled ? -1 : 0}
				onMouseDown={onTrackMouseDown}
				onKeyDown={onKeyDown}
				onFocus={() => {
					if (KeyboardMode.keyboardModeEnabled) {
						onThumbMouseEnter();
					}
				}}
				onBlur={onThumbMouseLeave}
				data-flx="ui.slider.slider-track.slider.key-down"
			>
				<div className={styles.track} data-flx="ui.slider.slider-track.track">
					{markerState.markerPositions.map((position, i) => {
						const markerValue = markerState.sortedMarkers[i];
						const renderedValue = onMarkerRender ? onMarkerRender(markerValue) : markerValue;
						const isFactoryDefaultValue = markerValue === factoryDefaultValue;
						return (
							<div
								key={`${markerValue}-${i}`}
								className={clsx(
									styles.mark,
									markerPosition === 'above' ? styles.markAbove : styles.markBelow,
									isFactoryDefaultValue && styles.defaultValue,
								)}
								style={{left: `${position}%`}}
								data-flx="ui.slider.slider-track.mark"
							>
								<div className={styles.markValue} data-flx="ui.slider.slider-track.mark-value">
									{renderedValue}
								</div>
								{renderedValue != null ? (
									<div className={styles.markDash} data-flx="ui.slider.slider-track.mark-dash" />
								) : (
									<div data-flx="ui.slider.slider-track.div" />
								)}
							</div>
						);
					})}
				</div>
				<div className={styles.bar} style={barStyles} data-flx="ui.slider.slider-track.bar">
					<div className={styles.barFill} style={combinedFillStyles} data-flx="ui.slider.slider-track.bar-fill" />
				</div>
				{children}
				<div className={styles.track} data-flx="ui.slider.slider-track.track--2">
					<div
						ref={thumbRef}
						className={styles.grabber}
						style={{left: valuePercent}}
						onMouseDown={onThumbMouseDown}
						onTouchStart={onThumbTouchStart}
						onMouseEnter={onThumbMouseEnter}
						onMouseLeave={onThumbMouseLeave}
						aria-hidden="true"
						data-slider-thumb="true"
						data-flx="ui.slider.slider-track.grabber.thumb-mouse-down"
					/>
				</div>
			</div>
		</FocusRing>
	);
}
